/**
 * Encyclopedia content access rules — what an *unentitled* visitor may read.
 *
 * Policy (see Flaw #2 plan + `_docs/13-provenance-and-canon-layers.md`):
 *  - **Open canon** is free for everyone (incl. anonymous): the SRD documents
 *    (SRD 5.2.1, SRD 5.1) and other open-canon systems (Dragon Slayer, Zweihänder,
 *    EvolveRPG).
 *  - **Grymwyrd / official / homebrew** content is **locked** behind a purchase:
 *    shown with a padlock that leads to a buy page. Owners (Atlas entitlement) see
 *    no lock.
 *
 * Two granularities:
 *  - {@link isLockedSelection} gates at the **facet** level (the system/setting/
 *    canon the visitor picked) — used to short-circuit a wholly-locked selection
 *    into the buy gate before any rows load.
 *  - {@link isEntityLocked} gates a **single entity** from its `provenance`
 *    (`setting_slugs`) plus the forward-compatible flat fields; defaults to *open*
 *    when untagged. Provenance is now available on both single-entity fetches and
 *    list rows (`?provenance=true`, batched — api PR #89), so a first-party entity
 *    locks correctly even inside an unfiltered/mixed list.
 *
 * World-specific policy (which systems are open, which settings are paid, which
 * books unlock them) comes from the active {@link WorldConfig}; this module is
 * world-agnostic. The open-SRD detection below is a universal platform rule.
 */
import { getActiveWorldConfig } from '@/lib/worldConfig'

/** Canon layers that are openly licensed and free for everyone (universal). */
export const OPEN_CANON_LAYERS = new Set<string>(['srd', 'open'])

/**
 * Loose match for an open-canon source identifier. The SRD 5.1 / 5.2.1 documents
 * are free for everyone, so an entity is open if ANY provenance signal names the
 * SRD or its versions — regardless of how the API slugs the system (`srd521`,
 * `srd-5.1`, a `dnd5e` SRD cut, a UUID containing `srd`, etc.). We must never gate
 * this content, so detection is deliberately permissive.
 */
const OPEN_CANON_PATTERN = /\bsrd\b|srd[-_ ]?5|\b5\.?1\b|\b5\.?2\.?1\b|open[-_ ]?(?:game|canon|licen[cs]e|ogl)/i

/**
 * Is this content open canon — free for everyone, NEVER gated? True when the
 * canon layer is SRD/open, or any source/system identifier names the SRD 5.1 /
 * 5.2.1, or the system is one of the active world's open-canon systems. This is
 * the authoritative free-content signal: it overrides every lock rule, so open
 * canon can never be accidentally paywalled (the user's hard requirement). A
 * first-party locked setting is never treated as open even if other fields are noisy.
 */
export function isOpenCanon(entity: { setting?: string; canon_layer?: string; game_system_id?: string }): boolean {
  const { lockedSettingIds, openCanonSystemIds } = getActiveWorldConfig()
  if (entity.setting && lockedSettingIds.includes(entity.setting)) return false
  const layer = entity.canon_layer?.toLowerCase()
  if (layer && (OPEN_CANON_LAYERS.has(layer) || OPEN_CANON_PATTERN.test(layer))) return true
  const sys = entity.game_system_id
  if (sys && (openCanonSystemIds.includes(sys) || OPEN_CANON_PATTERN.test(String(sys)))) return true
  return false
}

export interface FacetSelection {
  /** Selected game-system slug (rpgApi `system`). */
  system?: string
  /** Selected setting slug. */
  setting?: string
  /** Selected canon layer: `srd` | `official` | `homebrew`. */
  canon?: string
}

/**
 * Facet-level lock: would the *selected* slice of the encyclopedia contain
 * purchase-gated content? True when a locked setting (Grymwyrd/Greywick), a
 * non-open canon layer (`official`/`homebrew`), or a non-open game system is
 * chosen. Open-canon selections (and the unfiltered default) are never locked.
 */
export function isLockedSelection({ system, setting, canon }: FacetSelection): boolean {
  const { lockedSettingIds, openCanonSystemIds } = getActiveWorldConfig()
  // The open SRD canon (by system or by an explicit `srd` canon facet) is never
  // gated — even if paired with another facet.
  if (isOpenCanon({ game_system_id: system, canon_layer: canon, setting })) return false
  if (setting && lockedSettingIds.includes(setting)) return true
  if (canon === 'official' || canon === 'homebrew') return true
  if (system && !openCanonSystemIds.includes(system)) return true
  return false
}

/**
 * Per-entity lock from provenance. Open while the entity is untagged (e.g. a row
 * fetched without `?provenance=true`); when provenance is present (single-entity
 * OR a list row requested with `?provenance=true`) a Grymwyrd-setting / official /
 * homebrew / non-open-system / explicitly-locked entity becomes locked. Reads both
 * the live `provenance.setting_slugs` array and the forward-compatible flat fields.
 */
export function isEntityLocked(entity: {
  setting?: string
  canon_layer?: string
  game_system_id?: string
  is_locked?: boolean
  provenance?: { setting_slugs?: string[]; book_ids?: string[] }
}): boolean {
  const { lockedSettingIds, openCanonSystemIds } = getActiveWorldConfig()
  // Open SRD 5.1 / 5.2.1 canon is free for everyone — this wins over every lock
  // rule below (including a stray `is_locked`), so it can never be paywalled.
  if (isOpenCanon(entity)) return false
  if (entity.is_locked === true) return true
  if (entity.canon_layer === 'official' || entity.canon_layer === 'homebrew') return true
  if (entity.setting && lockedSettingIds.includes(entity.setting)) return true
  // Live per-entity signal: tagged to a paid first-party setting.
  if (entity.provenance?.setting_slugs?.some((s) => lockedSettingIds.includes(s))) return true
  if (entity.game_system_id && !openCanonSystemIds.includes(entity.game_system_id)) return true
  return false
}

/** Input to {@link requiredBookFor}: a facet selection and/or an entity's provenance. */
export type BookGateInput = FacetSelection & {
  setting?: string
  provenance?: { book_ids?: string[] }
}

/**
 * The RPGPublisher book id a visitor must own to unlock the given locked content.
 * Per-book: when the entity's `provenance.book_ids` names one of the active
 * world's unlock books, that book is required — so (e.g.) Galvanist content sends
 * the visitor to buy Galvanist, not Cataclysm. Falls back to the world's
 * configured `fallbackUnlockBookId` when no per-entity book is known.
 */
export function requiredBookFor(input: BookGateInput = {}): string {
  const { unlockBookIds, fallbackUnlockBookId } = getActiveWorldConfig()
  const gating = input.provenance?.book_ids?.find((id) => unlockBookIds.includes(id))
  return gating ?? fallbackUnlockBookId
}

/**
 * Per-owner lock: the entity is paid content AND the viewer does not own the
 * specific book that gates it. `ownedBookIds` is the viewer's entitlement set
 * (empty for guests). This is the authoritative per-card/detail lock signal in
 * the two-book world — owning Galvanist clears Galvanist locks but not Cataclysm.
 */
export function isEntityLockedForOwner(
  entity: Parameters<typeof isEntityLocked>[0] & { provenance?: { book_ids?: string[] } },
  ownedBookIds: readonly string[] = [],
): boolean {
  if (!isEntityLocked(entity)) return false
  return !ownedBookIds.includes(requiredBookFor(entity))
}
